import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { MDBPagination, MDBPaginationItem, MDBPaginationLink } from "mdb-react-ui-kit";

export default function PdfReactViewer({ file, className }) {
  const containerRef = useRef(null);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);

  // ---------- méret figyelés ----------
  useEffect(() => {
    const measure = () => {
      const w = containerRef.current?.clientWidth ?? 0;
      setContainerWidth(w);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // fájl váltásakor ugorjunk az első oldalra
  useEffect(() => {
    setPageNumber(1);
  }, [file]);

  // ---------- PDF betöltés ----------
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);
  const onDocumentLoadError = () => {
    setNumPages(undefined);
  };

  // ---------- kompakt pagináció ----------
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches;

  function buildCompactPages(total, current, { startCluster = 3, around = 1 } = {}) {
    if (!total || total < 1) return [];
    if (total <= startCluster + 2) {
      return Array.from({ length: total }, (_, i) => ({ t: "page", v: i + 1 }));
    }

    const out = [];
    const pushPage = (n) => out.push({ t: "page", v: n });
    const pushDots = () => out.push({ t: "dots" });
    const addRange = (a, b) => {
      for (let i = a; i <= b; i++) pushPage(i);
    };

    // eleje közelében
    if (current <= startCluster) {
      addRange(1, startCluster);
      pushDots();
      pushPage(total);
      return out;
    }
    // vége közelében
    if (current >= total - startCluster + 1) {
      pushPage(1);
      pushDots();
      addRange(total - startCluster + 1, total);
      return out;
    }
    // középen
    pushPage(1);
    pushDots();
    addRange(Math.max(2, current - around), Math.min(total - 1, current + around));
    pushDots();
    pushPage(total);
    return out;
  }

  const pagesToRender = useMemo(
    () =>
      buildCompactPages(numPages || 0, pageNumber, {
        startCluster: isMobile ? 3 : 4,
        around: isMobile ? 1 : 2,
      }),
    [numPages, pageNumber, isMobile],
  );

  const goto = (n) => {
    if (!numPages) return;
    if (n < 1 || n > numPages) return;
    setPageNumber(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className={`px-2 ${className || ""}`}>
      <div ref={containerRef}>
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          error={"Missing PDF file"}
          loading={"Upload file"}
        >
          <Page
            pageNumber={pageNumber}
            renderTextLayer={true}
            renderAnnotationLayer={false}
            width={containerWidth || undefined}
            scale={containerWidth ? undefined : 1}
          />
        </Document>

        {numPages > 1 && (
          <div className="d-flex align-items-center justify-content-center mt-2">
            <nav aria-label="PDF pagination">
              <MDBPagination className="mb-0 pagination-sm gap-1 pdf-pager">
                {/* Prev */}
                <MDBPaginationItem disabled={pageNumber <= 1}>
                  <MDBPaginationLink aria-label="Previous" onClick={() => goto(pageNumber - 1)}>
                    «
                  </MDBPaginationLink>
                </MDBPaginationItem>

                {/* pages + ellipszis */}
                {pagesToRender.map((it, idx) =>
                  it.t === "dots" ? (
                    <li
                      key={`dots-${idx}`}
                      className="ellipsis"
                      style={{
                        listStyle: "none",
                        padding: "4px 6px",
                        color: "#4f4f4f",
                        opacity: 0.7,
                        alignSelf: "center",
                      }}
                    >
                      …
                    </li>
                  ) : (
                    <MDBPaginationItem
                      key={it.v}
                      active={pageNumber === it.v}
                      aria-current={pageNumber === it.v ? "page" : undefined}
                    >
                      <MDBPaginationLink onClick={() => goto(it.v)}>
                        {it.v}
                        {pageNumber === it.v && <span className="visually-hidden">(current)</span>}
                      </MDBPaginationLink>
                    </MDBPaginationItem>
                  ),
                )}

                {/* Next */}
                <MDBPaginationItem disabled={pageNumber >= numPages}>
                  <MDBPaginationLink aria-label="Next" onClick={() => goto(pageNumber + 1)}>
                    »
                  </MDBPaginationLink>
                </MDBPaginationItem>
              </MDBPagination>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
